import {
  CheckoutFlowStep,
  isJetonWallet,
  selectCurrencyOptions,
  useAppSelector,
  useBanksRequest,
  useCancelPaymentRequest,
  useCheckoutPayment,
  useEventTracker,
  useSessionId,
  useUnloadBeacon
} from 'mmfintech-checkout-commons'
import type { CheckoutMethodField, CountryChoice, SupportedBank } from 'mmfintech-checkout-commons/types'
import { isEmptyString, isValidArray, isValidString, parseJson, tr, translateCountryName } from 'mmfintech-commons'
import { Button, ErrorDisplay, Input } from 'mmfintech-portal-commons'
import { useCallback, useEffect, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom'
import { CheckoutContainer, CheckoutContent, CustomPreloader, MerchantPane, SelectMethod } from '../../components'
import { DepositResult } from '../DepositResult'
import { WalletCheckout, WalletCheckoutOtp } from '../WalletCheckout'
import styles from './CheckoutPayment.module.scss'
import { CancelContainer, PaymentContainer } from './CheckoutPaymentCore.styled'

type CheckoutPaymentCoreProps = {
  pis?: boolean
}

export const CheckoutPaymentCore = ({ pis }: CheckoutPaymentCoreProps) => {
  const sessionId = useSessionId()

  const {
    logo,
    checkoutCountries,
    checkoutCountriesError,
    checkoutPay,
    checkoutPayError,
    checkoutPayFetching,
    session,
    sessionError,
    sessionFetching
  } = useSelector(
    ({
      checkout: {
        logo,
        checkoutCountries,
        checkoutCountriesError,
        checkoutPay,
        checkoutPayError,
        checkoutPayFetching,
        session,
        sessionError,
        sessionFetching
      }
    }) => ({
      logo,
      checkoutCountries,
      checkoutCountriesError,
      checkoutPay,
      checkoutPayError,
      checkoutPayFetching,
      session,
      sessionError,
      sessionFetching
    }),
    shallowEqual
  )

  const [triggered, setTriggered] = useState<boolean>(false)
  const [storageAccess, setStorageAccess] = useState<boolean>(false)

  const history = useHistory()
  useUnloadBeacon(sessionId)

  const currencies = useAppSelector(selectCurrencyOptions)

  const { trackEvent } = useEventTracker()
  const { cancelPayment } = useCancelPaymentRequest(pis)

  const {
    step,
    // setStep,
    fieldValues,
    status,
    countryCode,
    setCountryCode,
    paymentMethod,
    setPaymentMethod,
    selectedPaymentOption,
    handleStartOver,
    handlePay,
    handlePayWithWallet,
    initialized,
    jetonBankSpecialCase,
    handlePaymentStatusResponse
  } = useCheckoutPayment({
    pis: Boolean(pis),
    enableJetonWalletStep: true,
    preselectSingleMethod: true,
    initialStep: CheckoutFlowStep.SELECT_METHOD
  })

  const { banks, banksCount } = useBanksRequest()

  const handleCancelClick = () => {
    if (sessionId) {
      cancelPayment(sessionId)
    }
    trackEvent(sessionId, 'cancelled_by_payer')
    history.push(`${pis ? '/pis' : ''}/fail`)
    return false
  }

  const handleBankSelected = (code: string) => {
    trackEvent(sessionId, 'bank_selected', code)
  }

  const prepareResponse = useCallback(() => {
    if (session?.sessionStatus?.value === 'IN_PROGRESS') {
      return {
        result: parseJson(session?.paymentResult),
        resultType: session?.paymentResultType,
        processingAmount: session?.processingAmount,
        processingCurrency: session?.processingCurrency
      }
    }
    return checkoutPay
  }, [checkoutPay, session])

  const validBankSelection = () =>
    !fieldValues.hasBankSelection ||
    (fieldValues.hasBankSelection && banksCount === 1 && isValidString(fieldValues.getValue('bankChoiceId')))

  const validFields = () => {
    if (isJetonWallet(paymentMethod)) return true

    if (selectedPaymentOption) {
      const { fields } = selectedPaymentOption
      if (isValidArray(fields)) {
        const find = fields.find((item: CheckoutMethodField) => {
          const { name, prefilledValue, displayIfPrefilled } = item
          return (!prefilledValue?.length || displayIfPrefilled) && name !== 'bankChoiceId'
        })
        return !find
      }
    }
    return true
  }

  useEffect(() => {
    if (typeof document.hasStorageAccess === 'function') {
      document.hasStorageAccess().then(response => setStorageAccess(response))
    } else {
      setStorageAccess(true)
    }
  }, [])

  useEffect(() => {
    if (
      initialized &&
      !triggered &&
      !checkoutPayFetching &&
      isValidString(paymentMethod) &&
      validBankSelection() &&
      validFields()
    ) {
      setTriggered(true)
    }
    // eslint-disable-next-line
  }, [initialized, paymentMethod, fieldValues.getValue('bankChoiceId')])

  useEffect(() => {
    if (triggered) {
      handlePay()
    }
  }, [triggered])

  return (
    <CheckoutContainer>
      <CheckoutContent>
        <ErrorDisplay error={checkoutCountriesError} />

        {sessionFetching || !initialized ? (
          <PaymentContainer>
            <CustomPreloader />
          </PaymentContainer>
        ) : (
          <>
            {step !== CheckoutFlowStep.JETON_WALLET && <MerchantPane session={session} logo={logo} />}

            {(step === CheckoutFlowStep.ADDITIONAL_FIELDS ||
              step === CheckoutFlowStep.SELECT_BANK ||
              step === CheckoutFlowStep.SELECT_METHOD) && (
              <>
                <PaymentContainer>
                  {status === 'IDLE' && (
                    <>
                      {checkoutCountries?.length > 1 ? (
                        <div className='mb-3'>
                          <Input
                            type='select'
                            name='countryCode'
                            label={tr('CHECKOUT.PAYMENT.COUNTRY', 'Country')}
                            placeholder={tr('CHECKOUT.PAYMENT.COUNTRY_PLACEHOLDER', 'Select country')}
                            options={checkoutCountries?.map((country: CountryChoice) => ({
                              value: country.countryCode,
                              label: country.name
                            }))}
                            value={countryCode}
                            onChange={(_name: string, value: string) => setCountryCode(value)}
                            hideErrorLine
                            required
                          />
                        </div>
                      ) : (
                        <div className='mb-3'>
                          <Input
                            type='static'
                            label={tr('CHECKOUT.PAYMENT.COUNTRY', 'Country')}
                            value={translateCountryName(countryCode)}
                            hideErrorLine
                          />
                        </div>
                      )}

                      <SelectMethod
                        className='use-grid'
                        method={paymentMethod}
                        setMethod={setPaymentMethod}
                        countryCode={countryCode}
                        special={jetonBankSpecialCase}
                      />

                      {isValidArray(fieldValues.fields) && !isJetonWallet(paymentMethod) ? (
                        <>
                          <div className='mt-2' />

                          {fieldValues.fields.map((field: CheckoutMethodField) => {
                            if (field.name === 'bankChoiceId') {
                              return (
                                <Input
                                  key={field.name}
                                  type='select'
                                  label={tr('CHECKOUT.PAYMENT.BANK', 'Bank')}
                                  {...fieldValues.registerInput(field.name, handleBankSelected)}
                                  options={[
                                    ...(jetonBankSpecialCase
                                      ? [
                                          {
                                            value: 'JETON',
                                            label: tr('METADATA.PAYMENT_OPTIONS.JETON', 'Jeton')
                                          }
                                        ]
                                      : []),
                                    ...(Array.isArray(banks)
                                      ? banks.map((bank: SupportedBank) => {
                                          const { bankChoiceId, name } = bank
                                          return { value: bankChoiceId, label: name }
                                        })
                                      : [])
                                  ]}
                                  hideErrorLine
                                />
                              )
                            }

                            if (field.name === 'currency') {
                              return (
                                <Input
                                  key={field.name}
                                  type='select'
                                  label={
                                    field.translationKey
                                      ? tr(field.translationKey, field.defaultLabel)
                                      : field.defaultLabel
                                  }
                                  {...fieldValues.registerInput(field.name)}
                                  options={currencies?.map((currency: string) => {
                                    return { value: currency, label: currency }
                                  })}
                                  hideErrorLine
                                />
                              )
                            }

                            if (field.name === 'documentId' && paymentMethod === 'PIX') {
                              return (
                                <Input
                                  key={field.name}
                                  type={field.type || 'text'}
                                  label={
                                    field.translationKey
                                      ? tr(field.translationKey, field.defaultLabel)
                                      : field.defaultLabel
                                  }
                                  {...fieldValues.registerShort(field.name)}
                                  onChange={(name: string, value: string) =>
                                    fieldValues.setValue(name, value.replace(/[ ./-]/g, ''))
                                  }
                                  required={fieldValues.isFieldRequired(field)}
                                  hideErrorLine
                                  autoComplete='off'
                                />
                              )
                            }

                            return (
                              <Input
                                key={field.name}
                                type={field.sensitive ? 'password' : field.type || 'text'}
                                label={
                                  field.translationKey
                                    ? tr(field.translationKey, field.defaultLabel)
                                    : field.defaultLabel
                                }
                                {...fieldValues.registerInput(field.name)}
                                hideErrorLine
                                autoComplete='off'
                              />
                            )
                          })}
                        </>
                      ) : null}
                    </>
                  )}

                  <ErrorDisplay error={[sessionError, checkoutPayError]} />

                  {status === 'IDLE' && (
                    <div className='buttons'>
                      <Button
                        type='button'
                        color='primary'
                        text={tr('CHECKOUT.PAYMENT.BUTTON_PAY', 'Pay')}
                        onClick={handlePay}
                        disabled={isEmptyString(paymentMethod)}
                        loading={checkoutPayFetching}
                        data-test='button-pay'
                      />
                    </div>
                  )}
                </PaymentContainer>

                {session?.showCancelButton ? (
                  <CancelContainer>
                    <Link to='#' onClick={handleCancelClick}>
                      {tr('CHECKOUT.BUTTONS.CANCEL', 'Cancel')}
                    </Link>
                  </CancelContainer>
                ) : null}
              </>
            )}

            {step === CheckoutFlowStep.JETON_WALLET && (
              <WalletCheckout
                pis={pis}
                sessionId={sessionId}
                onCancel={handleStartOver}
                handlePay={handlePayWithWallet}
                paymentMethod={paymentMethod}
                hasStorageAccess={storageAccess}
                setStorageAccess={setStorageAccess}
              />
            )}

            {step === CheckoutFlowStep.WALLET_OTP && (
              <WalletCheckoutOtp onSuccess={response => handlePaymentStatusResponse(response)} wallet={true} />
            )}

            {step === CheckoutFlowStep.DEPOSIT_RESULT && (
              <DepositResult session={session} sessionId={sessionId} response={prepareResponse()} />
            )}

            {Boolean(session?.returnUrl) && (
              <div className={styles['checkout-footer']}>
                <a href={session?.returnUrl} target='_top'>
                  &larr; {tr('CHECKOUT.PAYMENT.BACK_TO_MERCHANT', 'Back to merchant')}
                </a>
              </div>
            )}
          </>
        )}
      </CheckoutContent>
    </CheckoutContainer>
  )
}
